/**
 * The roster by operational period: the current period's roster, or one of the
 * planned periods' rosters. Planned periods are identified by their period
 * number, which is unique within an incident.
 */

/** Current OP, one planned period by number, or every period at once. */
export type HwcgPeriodView = 'current' | 'all' | number;

export type HwcgPeriodChoice = {
  value: HwcgPeriodView;
  label: string;
};

export type HwcgPersonSeatStatus = {
  activation: 'accepted' | 'waiting' | 'declined' | null;
  checkedIn: boolean;
  signedIn: boolean;
};

export type HwcgSeatStatusCounts = {
  accepted: number;
  waiting: number;
  declined: number;
  checkedIn: number;
  signedIn: number;
};

type PeriodRosterMember = {
  userId: string;
  incidentPositions?: { activationStatus?: string | null }[];
};

/** Period 0 is the initial response, before any operational period starts. */
export function currentPeriodLabel(currentPeriodNumber: number): string {
  return currentPeriodNumber > 0
    ? `Current OP (OP ${currentPeriodNumber})`
    : 'Now (no operational period)';
}

export function plannedPeriodLabel(periodNumber: number): string {
  return `OP ${periodNumber}`;
}

/** Filter choices; empty when nothing is scheduled after the current period. */
export function rosterPeriodChoices(
  currentPeriodNumber: number,
  planned: readonly { periodNumber: number }[]
): HwcgPeriodChoice[] {
  if (planned.length === 0) return [];
  return [
    { value: 'current', label: currentPeriodLabel(currentPeriodNumber) },
    ...[...planned]
      .sort((a, b) => a.periodNumber - b.periodNumber)
      .map((period) => ({
        value: period.periodNumber,
        label: plannedPeriodLabel(period.periodNumber),
      })),
    { value: 'all', label: 'All' },
  ];
}

/**
 * Who the chart or table shows for a period. A planned period shows its own
 * roster. "All" shows the current roster plus anyone seated only on a later
 * period, labelled with the first period they are seated for.
 */
export function partitionRosterByPeriod<T extends { userId: string }>(params: {
  current: readonly T[];
  planned: readonly { periodNumber: number; members: readonly T[] }[];
  view: HwcgPeriodView;
}): { shown: T[]; later: T[]; labels: Record<string, string> } {
  const { current, planned, view } = params;
  if (view === 'current') return { shown: [...current], later: [], labels: {} };
  const ordered = [...planned].sort((a, b) => a.periodNumber - b.periodNumber);
  if (typeof view === 'number') {
    const period = ordered.find((item) => item.periodNumber === view);
    return { shown: [...(period?.members ?? [])], later: [], labels: {} };
  }
  const onCurrent = new Set(current.map((member) => member.userId));
  const seen = new Set<string>();
  const later: T[] = [];
  const labels: Record<string, string> = {};
  ordered.forEach((period) => {
    period.members.forEach((member) => {
      if (onCurrent.has(member.userId) || seen.has(member.userId)) return;
      seen.add(member.userId);
      later.push(member);
      labels[member.userId] = plannedPeriodLabel(period.periodNumber);
    });
  });
  return { shown: [...current], later, labels };
}

/** Collapses a seat's activation request state to what the roster shows. */
export function seatActivationState(
  status: string | null | undefined
): HwcgPersonSeatStatus['activation'] {
  if (!status) return null;
  if (status === 'accepted') return 'accepted';
  if (status === 'declined') return 'declined';
  return 'waiting';
}

/**
 * One person's answer across their seats on one roster: declined if any seat
 * was declined, accepted once every asked seat is accepted, waiting otherwise.
 */
export function personSeatActivation(
  member: PeriodRosterMember
): HwcgPersonSeatStatus['activation'] {
  const states = (member.incidentPositions ?? [])
    .map((position) => seatActivationState(position.activationStatus))
    .filter((state): state is NonNullable<typeof state> => state != null);
  if (states.length === 0) return null;
  if (states.includes('declined')) return 'declined';
  if (states.every((state) => state === 'accepted')) return 'accepted';
  return 'waiting';
}

export function hwcgSeatStatusCounts(
  userIds: readonly string[],
  people: Record<string, HwcgPersonSeatStatus> | null | undefined
): HwcgSeatStatusCounts | null {
  if (!people) return null;
  const counts: HwcgSeatStatusCounts = {
    accepted: 0,
    waiting: 0,
    declined: 0,
    checkedIn: 0,
    signedIn: 0,
  };
  let counted = 0;
  userIds.forEach((userId) => {
    const person = people[userId];
    if (!person?.activation) return;
    counted += 1;
    if (person.activation === 'accepted') counts.accepted += 1;
    else if (person.activation === 'declined') counts.declined += 1;
    else counts.waiting += 1;
    if (person.checkedIn) counts.checkedIn += 1;
    if (person.signedIn) counts.signedIn += 1;
  });
  return counted === 0 ? null : counts;
}

export function hwcgSeatStatusLine(counts: HwcgSeatStatusCounts): string {
  return `${counts.accepted} accepted · ${counts.waiting} waiting · ${counts.declined} declined · Check-in ${counts.checkedIn} · Sign-in ${counts.signedIn}`;
}

/** Matches the server: a seat cannot be asked again this soon after a request. */
export const SEAT_REQUEST_COOLDOWN_MS = 5 * 60 * 1000;

export type SeatRequestState =
  | { kind: 'not_sent' }
  | { kind: 'requested'; requestedAt: Date | null; cooldownEndsAt: Date | null }
  | { kind: 'no_reply'; requestedAt: Date | null; after: '24h' | '48h' }
  | { kind: 'accepted'; respondedAt: Date | null }
  | { kind: 'declined'; respondedAt: Date | null; note: string | null };

type SeatRequestPosition = {
  activationStatus?: string | null;
  activationRequestedAt?: string | null;
  activationRespondedAt?: string | null;
  activationNote?: string | null;
};

function parseTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const time = new Date(value);
  return Number.isNaN(time.getTime()) ? null : time;
}

/**
 * Where one seat's activation request stands. A request still within its
 * cooldown carries the time the Resend button unlocks.
 */
export function seatRequestState(
  position: SeatRequestPosition,
  now: number
): SeatRequestState {
  const status = position.activationStatus;
  const requestedAt = parseTime(position.activationRequestedAt);
  const respondedAt = parseTime(position.activationRespondedAt);
  if (!status) return { kind: 'not_sent' };
  if (status === 'accepted') return { kind: 'accepted', respondedAt };
  if (status === 'declined') {
    return {
      kind: 'declined',
      respondedAt,
      note: position.activationNote?.trim() || null,
    };
  }
  if (status === 'resend_24h' || status === 'resend_48h') {
    return {
      kind: 'no_reply',
      requestedAt,
      after: status === 'resend_24h' ? '24h' : '48h',
    };
  }
  const unlocksAt = requestedAt
    ? requestedAt.getTime() + SEAT_REQUEST_COOLDOWN_MS
    : null;
  return {
    kind: 'requested',
    requestedAt,
    cooldownEndsAt: unlocksAt && unlocksAt > now ? new Date(unlocksAt) : null,
  };
}

/** Seats on one roster nobody has asked yet, excluding the viewer's own. */
export function unsentSeatCount(
  members: readonly {
    userId: string;
    incidentPositions?: readonly {
      positionId: string;
      activationStatus?: string | null;
    }[];
  }[],
  options: { excludeUserId?: string | null; positionId?: string | null }
): number {
  let count = 0;
  members.forEach((member) => {
    if (member.userId === options.excludeUserId) return;
    (member.incidentPositions ?? []).forEach((position) => {
      if (options.positionId && position.positionId !== options.positionId)
        return;
      if (!position.activationStatus) count += 1;
    });
  });
  return count;
}

function formatWindowTime(value: string): string | null {
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) return null;
  return time.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** "Oct 29, 14:00 – Oct 30, 02:00", or what is missing from the window. */
export function periodWindowLabel(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  const from = start ? formatWindowTime(start) : null;
  const to = end ? formatWindowTime(end) : null;
  if (from && to) return `${from} – ${to}`;
  if (from) return `Starts ${from}`;
  if (to) return `Ends ${to}`;
  return 'No times set';
}
